import { useState, useCallback, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2 } from "lucide-react";

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export const SecurePdfViewer = ({ pdfData, title }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [loading, setLoading] = useState(true);

  // Disable keyboard shortcuts for copying/printing
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Block Ctrl+C, Ctrl+P, Ctrl+S, Ctrl+A
      if (e.ctrlKey && ["c", "p", "s", "a"].includes(e.key.toLowerCase())) {
        e.preventDefault();
        return false;
      }
      // Block Cmd+C, Cmd+P, Cmd+S, Cmd+A (Mac)
      if (e.metaKey && ["c", "p", "s", "a"].includes(e.key.toLowerCase())) {
        e.preventDefault();
        return false;
      }
      // Block F12 (DevTools)
      if (e.key === "F12") {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
  }, []);

  const onDocumentLoadError = useCallback((error) => {
    console.error("Error loading PDF:", error);
    setLoading(false);
  }, []);

  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(prev + 1, numPages || 1));
  };

  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.2, 2.5));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.2, 0.5));
  };

  if (!pdfData) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        Erro ao carregar PDF
      </div>
    );
  }

  return (
    <div
      className="flex flex-col h-full"
      style={{
        userSelect: "none",
        WebkitUserSelect: "none",
        MozUserSelect: "none",
        msUserSelect: "none",
      }}
      onContextMenu={(e) => e.preventDefault()}
      onCopy={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      data-testid="secure-pdf-viewer"
    >
      {/* Controls */}
      <div className="flex items-center justify-between p-3 border-b bg-secondary/30">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={goToPrevPage}
            disabled={pageNumber <= 1}
            data-testid="pdf-prev-page"
          >
            <ChevronLeft size={16} />
          </Button>
          <span className="text-sm font-mono min-w-[80px] text-center">
            {pageNumber} / {numPages || "..."}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={goToNextPage}
            disabled={pageNumber >= (numPages || 1)}
            data-testid="pdf-next-page"
          >
            <ChevronRight size={16} />
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={zoomOut}
            disabled={scale <= 0.5}
            data-testid="pdf-zoom-out"
          >
            <ZoomOut size={16} />
          </Button>
          <span className="text-sm font-mono min-w-[50px] text-center">
            {Math.round(scale * 100)}%
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={zoomIn}
            disabled={scale >= 2.5}
            data-testid="pdf-zoom-in"
          >
            <ZoomIn size={16} />
          </Button>
        </div>
      </div>

      {/* PDF Content */}
      <div
        className="flex-1 overflow-auto flex justify-center p-4 bg-muted/20"
        style={{
          // Additional protection
          WebkitTouchCallout: "none",
          WebkitUserDrag: "none",
        }}
      >
        {loading && (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        )}

        <Document
          file={pdfData}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          }
          className="pdf-document"
        >
          <Page
            pageNumber={pageNumber}
            scale={scale}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            loading={
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            }
            className="shadow-lg"
          />
        </Document>
      </div>

      {/* Page indicator for mobile */}
      <div className="md:hidden p-2 border-t text-center text-sm text-muted-foreground bg-background">
        Página {pageNumber} de {numPages || "..."}
      </div>
    </div>
  );
};

export default SecurePdfViewer;
